import { getFunctions, httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../firebase";

const functions = getFunctions();

/** Request a Mux direct upload URL from Cloud Functions (video only). */
export async function createMuxDirectUpload() {
  const callable = httpsCallable(functions, "createMuxDirectUpload");
  const result = await callable({});
  const data = result.data || {};
  if (!data.uploadUrl) {
    throw new Error(data.message || "Could not start Mux upload.");
  }
  return {
    uploadUrl: data.uploadUrl,
    uploadId: data.uploadId,
  };
}

/** Poll Mux upload / asset readiness for playback ID. */
export async function getMuxUploadStatus(uploadId) {
  const callable = httpsCallable(functions, "getMuxUploadStatus");
  const result = await callable({ uploadId });
  return result.data || {};
}

/**
 * Upload file to Mux direct upload URL with progress (0–100).
 * Mux direct uploads use a single PUT request.
 */
export function uploadFileToMux(uploadUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`Mux upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("Mux upload network error"));
    xhr.send(file);
  });
}

/** Upload photo or audio to Firebase Storage — never used for video. */
export function uploadMediaToStorage({ file, creatorId, folder, onProgress }) {
  const safeName = file.name.replace(/\s+/g, "-").toLowerCase();
  const path = `${folder}/${creatorId}/${Date.now()}-${safeName}`;
  const fileRef = ref(storage, path);
  const task = uploadBytesResumable(fileRef, file);

  return new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => {
        const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        onProgress?.(pct);
      },
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, storagePath: path });
      }
    );
  });
}

export function detectContentType(file) {
  const type = file.type || "";
  if (type.startsWith("video/")) return "video";
  if (type.startsWith("image/")) return "photo";
  if (type.startsWith("audio/")) return "audio";
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (["mp4", "mov", "m4v", "webm"].includes(ext)) return "video";
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return "photo";
  if (["mp3", "wav", "m4a", "aac", "flac"].includes(ext)) return "audio";
  return "photo";
}

export const UPLOAD_LIMITS = {
  video: { maxBytes: 8 * 1024 * 1024 * 1024, label: "MP4, MOV up to 8GB" },
  photo: { maxBytes: 50 * 1024 * 1024, label: "JPG, PNG up to 50MB" },
  audio: { maxBytes: 500 * 1024 * 1024, label: "MP3, WAV up to 500MB" },
};

export function validateMediaFile(file) {
  const contentType = detectContentType(file);
  const limit = UPLOAD_LIMITS[contentType];
  if (!limit) return { ok: false, error: "Unsupported file type." };
  if (file.size > limit.maxBytes) {
    return { ok: false, error: `File exceeds limit for ${contentType} (${limit.label}).` };
  }
  return { ok: true, contentType };
}

/** Wait for Mux asset to be ready (playback ID available). */
export async function waitForMuxReady(uploadId, { onProcessing, maxAttempts = 60, intervalMs = 3000 } = {}) {
  onProcessing?.();
  for (let i = 0; i < maxAttempts; i++) {
    const status = await getMuxUploadStatus(uploadId);
    if (status.playbackId) {
      return {
        muxPlaybackId: status.playbackId,
        muxAssetId: status.assetId || null,
        thumbnailUrl: status.thumbnailUrl || null,
      };
    }
    if (status.error) throw new Error(status.error);
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Video processing timed out. Try again in a few minutes.");
}

export function formatFileSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}
